/**
 * Icon System Type Definitions
 *
 * This module defines the type system for the centralized icon library.
 * All icons are referenced using semantic names (IconName enum) rather than
 * implementation-specific names from the underlying icon library.
 *
 * @example
 * ```tsx
 * import { Icon, IconName } from '@/icons';
 *
 * function MyComponent() {
 *   return <Icon name={IconName.DASHBOARD} size="md" />;
 * }
 * ```
 */

import { LucideIcon } from 'lucide-react';

/**
 * Semantic icon names used throughout the application.
 *
 * These names are implementation-agnostic and describe the icon's purpose
 * rather than its visual appearance. This abstraction allows us to swap
 * icon libraries without changing component code.
 *
 * When adding new icons:
 * 1. Add the semantic name here
 * 2. Map it to a Lucide component in registry.ts
 * 3. Use IconName.YOUR_NEW_ICON in components
 */
export enum IconName {
  // Navigation & Layout
  DASHBOARD = 'dashboard',
  KANBAN = 'kanban',
  FOLDER = 'folder',
  MENU = 'menu',
  CALENDAR = 'calendar',

  // User & Authentication
  USER = 'user',
  USERS = 'users',
  USER_CHECK = 'user-check',
  USER_SEARCH = 'user-search',
  SEARCH = 'search',

  // Education & Learning
  BUILDING = 'building',
  GRADUATION_CAP = 'graduation-cap',
  BOOK_OPEN = 'book-open',
  SCHOOL = 'school',

  // UI Controls
  CHECK = 'check',
  CHEVRON_DOWN = 'chevron-down',
  CHEVRON_UP = 'chevron-up',
  CHEVRON_LEFT = 'chevron-left',
  CHEVRON_RIGHT = 'chevron-right',
  X = 'x',
  PLUS = 'plus',
  MINUS = 'minus',

  // Actions
  EDIT = 'edit',
  LINK = 'link',
  SAVE = 'save',
  SETTINGS = 'settings',
  TRASH = 'trash',
  CORNER_DOWN_RIGHT = 'corner-down-right',

  // Status & Feedback
  ALERT_CIRCLE = 'alert-circle',
  INFO = 'info',
  BELL = 'bell',

  // Theme & Display
  SUN = 'sun',
  MOON = 'moon',
  MONITOR = 'monitor',
  PALETTE = 'palette',
  SPARKLES = 'sparkles',
  GEM = 'gem',
  ICE_CREAM = 'ice-cream',
  TREE_PINE = 'tree-pine',
  HEART = 'heart',
  DROPLETS = 'droplets',
  LEAF = 'leaf',
  FLOWER = 'flower',
  WAVES = 'waves',
  CLOUD = 'cloud',
  FLAME = 'flame',

  // Sizing Controls
  A_LARGE = 'a-large',
  A_SMALL = 'a-small',

  // Loading & Status
  LOADER = 'loader',

  // Tags & Organization
  TAG = 'tag',

  // Auth Actions
  LOG_OUT = 'log-out',
  EYE = 'eye',
  EYE_OFF = 'eye-off',

  // Board Templates
  COPY = 'copy',
  LAYOUT_TEMPLATE = 'layout_template',
  REFRESH = 'refresh',

  // Analytics
  ACTIVITY = 'activity',

  // Jobs
  ZAP = 'zap',

  // Media
  PLAY = 'play',

  // Links & External
  EXTERNAL_LINK = 'external-link',
}

/**
 * Icon size options mapped to design tokens.
 *
 * - 'sm': Small icons (12px base)
 * - 'md': Medium icons (16px base) - default
 * - 'lg': Large icons (20px base)
 * - 'xl': Extra large icons (24px base)
 *
 * Actual pixel sizes may vary based on user's sizing multiplier preference.
 */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Props for the Icon component.
 */
export interface IconProps extends Omit<
  React.SVGProps<SVGSVGElement>,
  'name' | 'size'
> {
  /**
   * The semantic name of the icon to render.
   */
  name: IconName;

  /**
   * Size of the icon. Maps to design tokens.
   * @default 'md'
   */
  size?: IconSize;

  /**
   * Custom color for the icon. If not provided, uses theme default.
   */
  color?: string;

  /**
   * Additional CSS classes to apply.
   */
  className?: string;
}

/**
 * Icon registry type mapping icon names to Lucide components.
 */
export type IconRegistry = Record<IconName, LucideIcon>;

/**
 * Configuration for the IconProvider context.
 */
export interface IconContextValue {
  /**
   * Default icon size to use when size prop is not specified.
   * @default 'md'
   */
  defaultSize: IconSize;
}
